import { Component, computed, input, output } from '@angular/core';
import { ProductKeys } from '@config/keys/product.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { FirstLinePipe } from '@core/pipes/first-line.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { TagAtomComponent } from '@shared/atoms/tag-atom/tag-atom.component';
import { RadialGradientDirective } from '@shared/directives/radial-gradient.directive';
import { VisibleLabelsPipe } from '@shared/pipes/visible-product-labels/visible-product-labels.pipe';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import { hasDiscountPromotionLabel } from '@core/services/eligible-promotions-labels/has-discount-promotion-label.util';
import { KioskProductCardInputs } from '../kiosk-product-card-factory/kiosk-product-cart';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';

@Component({
  selector: 'app-kiosk-product-card-v4-atom',
  templateUrl: './kiosk-product-card-v4-atom.component.html',
  imports: [
    ContentBuilderColorPipe,
    TranslatePipe,
    PriceAtomComponent,
    TagAtomComponent,
    VisibleLabelsPipe,
    RadialGradientDirective,
    ButtonAtomComponent,
    FirstLinePipe,
  ],
  styles: `
    :host {
      @apply p-2 h-full rounded-sm relative;
    }
    :host.borderedCard {
      @apply rounded border border-solid border-neutral-300 bg-neutral-50 p-1;
      > div {
        @apply rounded p-1;
      }
    }

    :host.shadowCard {
      box-shadow: var(--kiosk-card-shadow);
      @apply rounded p-2;
    }

    :host.roundedCard {
      @apply rounded;
    }
  `,
  // Host has p-2 — template bg only covers the inner div, not host padding.
  host: {
    '[style.background-color]': '_cardColor() || "#ffffff"',
  },
})
export class KioskProductCardV4AtomComponent implements KioskProductCardInputs {
  readonly product = input.required<DoodProductModel>();
  readonly quantity = input.required<number | undefined>();
  readonly isProductAvailable = input<boolean | undefined>(true);
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly cardColor = input<IContentBuilderFieldColor>();

  readonly _cardColor = computed(() => fromContentBuilderColor(this.cardColor(), '#ffffff'));

  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly priceColor = input<IContentBuilderFieldColor>();
  readonly isShowProductIcon = input<boolean>(true);

  readonly addProduct = output<void>();
  readonly showProduct = output<void>();

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  displayDiscount = computed(() => {
    const product = this.product();
    const discount = product[ProductKeys.Discount] ?? 0;
    return discount > 0;
  });

  discountPercentage = computed(() => {
    const product = this.product();
    const originalPrice = product[ProductKeys.Price];
    const discount = product[ProductKeys.Discount] ?? 0;

    if (originalPrice <= 0 || discount <= 0 || originalPrice <= discount) {
      return 0;
    }

    const discountAmount = originalPrice - discount;
    const discountPercentage = Math.round((discountAmount / originalPrice) * 100);
    return discountPercentage;
  });

  isAvailable = computed(() => {
    const product = this.product();
    const explicitAvailability = this.isProductAvailable();

    // If explicitly set to false via input, respect that
    if (!explicitAvailability) {
      return false;
    }

    // Check product's own availability properties
    const productAvailable = product[ProductKeys.Available] !== false;
    const shopAvailable = product[ProductKeys.ShopAvailable] !== false;
    const hasInventory = product[ProductKeys.InventoryTracked]
      ? (product[ProductKeys.InventoryAvailable] ?? 0) > 0
      : true;

    return productAvailable && shopAvailable && hasInventory;
  });

  addToCart(): void {
    this.addProduct.emit();
  }

  showProductModal(): void {
    this.showProduct.emit();
  }

  protected readonly _hasDiscountPromotionLabel = computed(() =>
    hasDiscountPromotionLabel(this.product()),
  );
}
